import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import { SearchDialog } from "@/components/search/SearchDialog";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/asd", label: "A.S.D." },
  { href: "/academy", label: "Academy" },
  { href: "/percorsi", label: "Percorsi" },
  { href: "/maestri", label: "Maestri" },
  { href: "/vita-in-palestra", label: "Palestra" },
  { href: "/blog", label: "Blog" },
  { href: "/contatti", label: "Contatti" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) =>
    location.pathname === href ||
    (href === "/blog" && location.pathname.startsWith("/blog"));

  // Global keyboard shortcut: ⌘K / Ctrl+K / "/"
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "/" && !isEditable) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#000] border-b border-gold/20">
      <nav className="container-wide mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Taekwondo Mattei" width={160} height={64} fetchPriority="high" decoding="async" className="h-14 md:h-16 w-auto" />
            <span className="text-white font-heading text-lg md:text-xl font-bold tracking-wide">
              Taekwondo Mattei Roma
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "font-body text-sm font-medium transition-colors link-underline",
                  isActive(link.href) ? "text-gold" : "text-gold/70 hover:text-gold"
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Search + CTA - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-full text-gold/70 hover:text-gold hover:bg-gold/5 transition-colors"
              aria-label="Apri ricerca"
              title="Cerca (⌘K)"
            >
              <Search size={20} />
            </button>
            <Link to="/lezione-di-prova" className="btn-accent text-sm px-6 py-3">
              Prenota Lezione
            </Link>
          </div>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-1">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 text-gold"
              aria-label="Apri ricerca"
            >
              <Search size={22} />
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-gold"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-gold/20 animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    "font-body text-base font-medium py-3 px-4 rounded-lg transition-colors",
                    isActive(link.href)
                      ? "text-gold bg-gold/10"
                      : "text-gold/70 hover:text-gold hover:bg-gold/5"
                  )}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/lezione-di-prova"
                onClick={() => setIsOpen(false)}
                className="btn-accent text-center mt-4"
              >
                Prenota Lezione di Prova
              </Link>
            </div>
          </div>
        )}
      </nav>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
};
